import { useState } from 'react';
import { Icone } from '../components/Icone';
import { IconePleine } from '../components/IconePleine';
import { useToast } from '../components/ui/useToast';
import { BandeauEtatIa } from '../components/usage-ia/BandeauEtatIa';
import { ErreurApi } from '../lib/api';
import {
  HISTORIQUE_TUTEUR_MAX,
  LONGUEUR_ECHANGE_TUTEUR_MAX,
  useEtatIa,
  useQuestionTuteur,
  type EchangeTuteur,
} from '../lib/useUsageIa';

/**
 * Tuteur IA (écran `ai-tutor` de la maquette validée,
 * `docs/maquette/modules/04-pages-pedagogie-player.js`).
 *
 * La question passe par la passerelle IA de l'API (TF-439), qui la compte — au
 * compte et à la formation de l'apprenant — et la refuse quand la direction a
 * coupé l'IA, ou ne l'a pas ouverte au compte : elle est fermée de base, la
 * direction accorde un quota (TF-474). Sans prestataire raccordé, l'écran le dit, sans conversation
 * ni réponse inventées. La conversation n'est gardée nulle part : l'écran
 * renvoie ses derniers échanges avec chaque question, et elle s'efface au
 * départ de la page.
 */

const SUGGESTIONS: { icone: 'ampoule' | 'stylo' | 'cible' | 'livre'; texte: string }[] = [
  { icone: 'ampoule', texte: 'Explique-moi avec un exemple plus simple' },
  { icone: 'stylo', texte: 'Fais-moi un quiz sur ce sujet' },
  { icone: 'cible', texte: 'Donne-moi un exercice pratique' },
  { icone: 'livre', texte: 'Quel cours dois-je revoir ?' },
];

interface Message {
  de: 'moi' | 'tuteur';
  texte: string;
  sceau?: string;
}

export function TuteurIaPage(): JSX.Element {
  const notifier = useToast();
  const { data: etat } = useEtatIa();
  const tuteur = useQuestionTuteur();
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);

  const ligne = etat?.fonctions.find((fonction) => fonction.fonction === 'TUTEUR');
  const raccorde = ligne?.raccordee ?? false;
  const etatCompte = etat?.compte?.etat ?? 'AUTORISE';
  const desactive = etatCompte === 'DESACTIVE' || Boolean(etat?.formationApprenant?.desactivee);
  const fermePendantQuiz = etat?.tuteurFerme ?? null;
  const statut = !raccorde
    ? 'Non raccordé'
    : desactive
      ? 'Désactivé par la direction'
      : etatCompte === 'NON_AUTORISE'
        ? 'Non ouvert à votre compte'
        : etatCompte === 'QUOTA_ATTEINT'
          ? 'Quota du jour atteint'
          : fermePendantQuiz
            ? 'Fermé pendant votre quiz'
            : 'Disponible';
  const ferme = desactive || etatCompte !== 'AUTORISE' || fermePendantQuiz !== null;

  const envoyer = (): void => {
    const texte = question.trim();
    if (!texte || tuteur.isPending) return;
    if (!raccorde) {
      notifier("Le tuteur IA n'est pas encore raccordé : votre question n'a pas été envoyée.");
      return;
    }
    if (etatCompte === 'NON_AUTORISE') {
      notifier('L’IA n’est pas ouverte à votre compte : votre question n’a pas été envoyée.');
      return;
    }
    if (fermePendantQuiz) {
      notifier(fermePendantQuiz.motif);
      return;
    }
    // Un échange trop long est laissé de côté plutôt que tronqué : tronquée, une réponse perdrait son sceau.
    const historique = messages
      .filter((message) => message.texte.length <= LONGUEUR_ECHANGE_TUTEUR_MAX)
      .slice(-HISTORIQUE_TUTEUR_MAX)
      .map(
        (message): EchangeTuteur =>
          message.de === 'moi'
            ? { role: 'QUESTION', texte: message.texte }
            : { role: 'REPONSE', texte: message.texte, sceau: message.sceau },
      );
    setQuestion('');
    setMessages((avant) => [...avant, { de: 'moi', texte }]);
    tuteur.mutate(
      { question: texte, historique },
      {
        onSuccess: (reponse) =>
          setMessages((avant) => [...avant, { de: 'tuteur', texte: reponse.texte, sceau: reponse.sceau }]),
        onError: (erreur) => notifier(erreur instanceof ErreurApi ? erreur.message : 'Le tuteur n’a pas pu répondre.'),
      },
    );
  };

  return (
    <div className="mx-auto h-full max-w-[1500px] space-y-4 p-4 sm:p-6">
      {etat && raccorde && <BandeauEtatIa etat={etat} fonction="TUTEUR" />}
      <div className="grid grid-cols-1 gap-6 lg:h-[calc(100vh-160px)] lg:grid-cols-4">
        <div className="space-y-4 lg:col-span-1 min-w-0">
          <div className="rounded-2xl bg-white p-4 sm:p-5 shadow-card">
            <div className="mb-4 flex items-center gap-3">
              <div className="gradient-mint flex h-12 w-12 items-center justify-center rounded-xl text-white">
                <Icone nom="ia" className="h-6 w-6" />
              </div>
              <div>
                <h1 className="font-display font-bold text-codialis-navy">Tuteur IA</h1>
                <div className="flex items-center gap-1 text-xs font-semibold text-codialis-gray">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${raccorde && !ferme ? 'bg-codialis-mint' : 'bg-codialis-gray'}`}
                  />
                  {statut}
                </div>
              </div>
            </div>
            <p className="text-xs leading-relaxed text-codialis-gray">
              {raccorde
                ? 'Une aide pour comprendre et s’exercer : vérifiez toujours ses réponses auprès de votre formateur.'
                : 'Une fois un prestataire retenu, le tuteur s’appuiera sur vos cours, le catalogue et la documentation officielle — toujours en français, hébergé en France.'}
            </p>
          </div>

          <div className="rounded-2xl bg-white p-4 sm:p-5 shadow-card">
            <h2 className="font-display mb-3 text-sm font-bold text-codialis-navy">Conversations récentes</h2>
            <p className="text-sm text-codialis-gray">
              Aucune conversation n’est gardée : elle s’efface quand vous quittez la page.
            </p>
          </div>
        </div>

        <div className="flex min-h-[480px] flex-col rounded-2xl bg-white shadow-card lg:col-span-3 min-w-0">
          <div className="border-b border-gray-100 px-4 sm:px-6 py-4">
            <h2 className="font-display font-bold text-codialis-navy">Nouvelle question</h2>
          </div>

          {messages.length === 0 ? (
            <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-3 p-4 sm:p-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-codialis-gray">
                <Icone nom="ia" className="h-6 w-6" />
              </div>
              <p className="max-w-sm text-sm text-codialis-gray">
                {raccorde
                  ? 'Posez votre question : la réponse s’affichera ici.'
                  : 'Le tuteur IA n’est pas encore raccordé à un modèle. Cette fonctionnalité sera activée une fois le prestataire choisi.'}
              </p>
            </div>
          ) : (
            <ol className="min-w-0 flex-1 space-y-3 overflow-y-auto p-4 sm:p-6" aria-live="polite">
              {messages.map((message, index) => (
                <li
                  key={index}
                  className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-sm ${
                    message.de === 'moi' ? 'ml-auto bg-codialis-navy text-white' : 'bg-gray-50 text-codialis-navy'
                  }`}
                >
                  {message.texte}
                </li>
              ))}
              {tuteur.isPending && (
                <li className="max-w-[85%] rounded-2xl bg-gray-50 px-4 py-2.5 text-sm text-codialis-gray">
                  Le tuteur rédige sa réponse…
                </li>
              )}
            </ol>
          )}

          <div className="flex flex-wrap gap-2 px-4 sm:px-6 pb-3">
            {SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion.texte}
                type="button"
                onClick={() => setQuestion(suggestion.texte)}
                className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-codialis-navy transition hover:bg-codialis-mint/10 hover:text-codialis-mint"
              >
                <Icone nom={suggestion.icone} className="h-3.5 w-3.5" />
                {suggestion.texte}
              </button>
            ))}
          </div>

          <div className="border-t border-gray-100 p-4">
            <div className="flex items-end gap-2 rounded-2xl bg-gray-50 p-2 pl-4 sm:gap-3">
              <label className="sr-only" htmlFor="question-tuteur">
                Votre question au tuteur IA
              </label>
              <textarea
                id="question-tuteur"
                rows={1}
                maxLength={2000}
                value={question}
                onChange={(evenement) => setQuestion(evenement.target.value)}
                onKeyDown={(evenement) => {
                  if (evenement.key === 'Enter' && !evenement.shiftKey) {
                    evenement.preventDefault();
                    envoyer();
                  }
                }}
                placeholder="Posez votre question au tuteur IA…"
                className="min-w-0 flex-1 resize-none border-0 bg-transparent py-2 text-sm focus:outline-none"
              />
              <button
                type="button"
                aria-label="Joindre un fichier"
                onClick={() => notifier('Envoi de fichier non raccordé')}
                className="rounded-lg p-2 text-codialis-gray hover:text-codialis-navy"
              >
                <Icone nom="trombone" className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Dicter la question"
                onClick={() => notifier('Dictée vocale non raccordée')}
                className="rounded-lg p-2 text-codialis-gray hover:text-codialis-navy"
              >
                <Icone nom="micro" className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Envoyer la question"
                onClick={envoyer}
                disabled={tuteur.isPending || ferme}
                className="gradient-mint flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white transition hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <IconePleine nom="envoyer" className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 px-2 text-[10px] text-codialis-gray">
              <span className="flex items-center gap-1">
                <Icone nom="cadenas" className="h-3 w-3" />
                Ni la question ni la réponse ne sont gardées · chaque requête est comptée
              </span>
              <span>Modèle : {raccorde ? (ligne?.prestataire ?? 'raccordé') : 'non raccordé'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
